import parse, { attributesToProps, domToReact } from "html-react-parser";
import HostedVideoEmbed from "../Components/Common/HostedVideoEmbed.jsx";
import ChatLinkCard from "../Components/Common/ChatLinkCard.jsx";
import {
  isEmbeddableHostedVideoLink,
  isExtractableChatLink,
  isHostedBookmarkCardLink,
} from "./videoEmbedUrl";

function removeAnchorWithAdjacentBreaks(anchor) {
  const prev = anchor.previousSibling;
  const next = anchor.nextSibling;
  anchor.remove();

  if (next && next.nodeType === 1 /* ELEMENT_NODE */ && next.tagName === "BR") {
    next.remove();
  } else if (
    next &&
    next.nodeType === 3 /* TEXT_NODE */ &&
    !next.textContent.trim()
  ) {
    next.remove();
  } else if (
    prev &&
    prev.nodeType === 1 &&
    prev.tagName === "BR" &&
    !prev.previousSibling
  ) {
    prev.remove();
  }
}

function isEmptyRichTextBlock(el) {
  const tag = el.tagName?.toLowerCase();
  if (tag !== "p" && tag !== "div") return false;
  return (
    !el.textContent.trim() && !el.querySelector("img, iframe, video, audio, a")
  );
}

function createBlankLineMarker() {
  const el = document.createElement("div");
  el.className = "post-text-blank-line";
  el.setAttribute("aria-hidden", "true");
  return el;
}

function finalizeExtractedHtml(tpl) {
  Array.from(tpl.content.querySelectorAll("p, div")).forEach((el) => {
    if (el.classList?.contains("post-text-blank-line")) return;
    if (isEmptyRichTextBlock(el)) {
      el.replaceWith(createBlankLineMarker());
    }
  });

  Array.from(tpl.content.querySelectorAll("span")).forEach((el) => {
    if (
      !el.textContent.trim() &&
      !el.querySelector("img, iframe, video, audio, a")
    ) {
      el.remove();
    }
  });

  const wrapper = document.createElement("div");
  wrapper.appendChild(tpl.content);
  return wrapper.innerHTML;
}

/**
 * Use one handler instance per `parse()` call so sibling keys stay unique.
 * When `keepLinkAnchorsInline` is true, link/bookmark anchors are left as plain
 * clickable links instead of being turned into cards — used when the card is
 * already rendered separately (from `extractHostedVideoEmbeds`'s `linkUrls`) and
 * we still want the raw link visible in the caption.
 */
export function createVideoAnchorReplaceHandler({
  keepLinkAnchorsInline = false,
} = {}) {
  let seq = 0;
  return (domNode) => {
    if (domNode.name !== "a" || !domNode.attribs?.href) return undefined;
    const href = domNode.attribs.href;
    seq += 1;
    if (isEmbeddableHostedVideoLink(href)) {
      return (
        <div className="hosted-video-embed-block" key={`hve-${seq}`}>
          <HostedVideoEmbed url={href} />
        </div>
      );
    }
    if (isHostedBookmarkCardLink(href)) {
      if (keepLinkAnchorsInline) return undefined;
      return (
        <div className="hosted-video-embed-block" key={`hve-${seq}`}>
          <HostedVideoEmbed url={href} />
        </div>
      );
    }
    if (isExtractableChatLink(href)) {
      if (keepLinkAnchorsInline) return undefined;
      return <ChatLinkCard url={href} key={`link-${seq}`} />;
    }
    return undefined;
  };
}

/**
 * Rich text often wraps content in `<p>`. Video embeds are block-level; nesting
 * them inside `<p>` is invalid HTML and causes React DOM nesting / hydration warnings.
 * Optional `extraReplace` runs first (e.g. mention spans in tribe Card/Comment).
 */
export function createParsedHtmlParseOptions(
  extraReplace,
  { keepLinkAnchorsInline = false } = {},
) {
  const replaceVideo = createVideoAnchorReplaceHandler({
    keepLinkAnchorsInline,
  });
  const options = {
    replace(domNode) {
      if (typeof extraReplace === "function") {
        const extra = extraReplace(domNode, options);
        if (extra !== undefined) return extra;
      }
      const embedded = replaceVideo(domNode);
      if (embedded !== undefined) return embedded;
      if (domNode.name === "p" || domNode.name === "div") {
        const children = domNode.children || [];
        const hasVisibleContent = children.some((child) => {
          if (child.type === "text") return child.data.trim().length > 0;
          if (child.type === "tag") {
            const name = child.name?.toLowerCase();
            return name !== "br";
          }
          return false;
        });
        if (!hasVisibleContent) {
          return <div className="post-text-blank-line" aria-hidden="true" />;
        }
      }
      if (domNode.name === "p") {
        const props = attributesToProps(domNode.attribs || {});
        const mergedClass = [props.className].filter(Boolean).join(" ");
        return (
          <div {...props} className={mergedClass || undefined}>
            {domToReact(domNode.children, options)}
          </div>
        );
      }
      return undefined;
    },
  };
  return options;
}

export function parseLinkifiedHtmlWithVideoEmbeds(htmlString, options) {
  if (htmlString == null || htmlString === "") return null;
  return parse(htmlString, createParsedHtmlParseOptions(undefined, options));
}

/** True when the html still has any human-readable text (after stripping tags). */
export function hasMeaningfulHtml(value = "") {
  if (!value) return false;
  const plain = String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim();
  return plain.length > 0;
}

/**
 * Split a linkified HTML string into:
 * - `videoUrls`: ordered hrefs of embeddable hosted-video links found inside <a>
 * - `linkUrls`: ordered hrefs of generic http(s) links (bookmark cards)
 * - `textHtml`: the remaining HTML (with those anchors removed, and any now-empty
 *   wrapper paragraphs / `<br>` separators cleaned up so we don't leave a blank
 *   bubble behind)
 * - `hasText`: convenience flag — true if `textHtml` still has visible text
 *
 * Used by chat bubbles so embeds render as standalone blocks (matching uploaded
 * video UI) instead of being trapped inside the `.my-text-chat` bubble.
 */
export function extractHostedVideoEmbeds(
  htmlString,
  { keepLinkAnchorsInline = false } = {},
) {
  if (htmlString == null || htmlString === "") {
    return { videoUrls: [], linkUrls: [], textHtml: "", hasText: false };
  }
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {
      videoUrls: [],
      linkUrls: [],
      textHtml: String(htmlString),
      hasText: hasMeaningfulHtml(htmlString),
    };
  }

  const tpl = document.createElement("template");
  tpl.innerHTML = String(htmlString);

  const anchors = Array.from(tpl.content.querySelectorAll("a[href]"));
  const videoUrls = [];
  const linkUrls = [];

  anchors.forEach((a) => {
    const href = a.getAttribute("href");
    if (!href) return;

    if (isEmbeddableHostedVideoLink(href)) {
      videoUrls.push(href);
      removeAnchorWithAdjacentBreaks(a);
      return;
    }

    if (isHostedBookmarkCardLink(href) || isExtractableChatLink(href)) {
      linkUrls.push(href);
      // When keeping links inline, leave the anchor in place so the raw link
      // still shows in the caption alongside the standalone card. Otherwise
      // strip it so only the card renders.
      if (!keepLinkAnchorsInline) {
        removeAnchorWithAdjacentBreaks(a);
      }
    }
  });

  // Fallback: bare URLs linkify may miss (e.g. draft HTML edge cases)
  const bareUrls = String(htmlString).match(/https?:\/\/[^\s<>"']+/g) || [];
  bareUrls.forEach((raw) => {
    const url = raw.replace(/[.,!?;)]+$/, "");
    if (
      isEmbeddableHostedVideoLink(url) &&
      !videoUrls.includes(url) &&
      !linkUrls.includes(url)
    ) {
      videoUrls.push(url);
    }
  });

  const textHtml = finalizeExtractedHtml(tpl);

  return {
    videoUrls,
    linkUrls,
    textHtml,
    hasText: hasMeaningfulHtml(textHtml),
  };
}
